/**
 * Brand-owner switch for "Build with AI" in the page builder.
 *
 * Off by default, and deliberately a separate decision from whether the
 * server has an API key. Generation is billed to the platform rather than to
 * the brand, so this is the control that decides who can spend that — a key
 * dropped into an env file should not switch the feature on for every brand
 * at once.
 *
 * When the platform has no key configured the switch is disabled and says so,
 * rather than letting an owner turn on something that cannot work.
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';

export default function AiToggle({ brandData, fetchData }) {
  const brandId = brandData?._id;
  const [on, setOn] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [quota, setQuota] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (brandData) setOn(!!brandData.ai_enabled);
  }, [brandData]);

  useEffect(() => {
    if (!brandId) return;
    API.aiSiteStatus({ brand_id: brandId })
      .then((res) => { setConfigured(res?.configured !== false); setQuota(res?.quota || null); })
      // Assume configured on a failed check: showing "unavailable" because a
      // status call hiccuped would be more confusing than the real error the
      // owner gets if they try to use it.
      .catch(() => setConfigured(true));
  }, [brandId]);

  const save = (next) => {
    if (!brandId) return;
    setOn(next);
    setSaving(true);
    const fd = new FormData();
    fd.append('data', JSON.stringify({ brand_id: brandId, ai_enabled: next }));
    API.updateBrandData(fd)
      .then(() => { toast.success(next ? 'AI turned on' : 'AI turned off'); fetchData && fetchData(); })
      .catch((err) => { setOn(!next); toast.error(err?.message || 'Failed'); })
      .finally(() => setSaving(false));
  };

  return (
    <Card sx={{ p: '30px', py: '28px' }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Stack direction='row' alignItems='flex-start' justifyContent='space-between' gap='16px'>
          <Box>
            <Stack direction='row' alignItems='center' spacing='8px' mb='4px'>
              <AutoAwesomeIcon fontSize='small' color='primary' />
              <Typography variant='h6' fontWeight='bold'>Build with AI</Typography>
              {!configured && <Chip size='small' color='warning' label='Not available yet' />}
            </Stack>
            <Typography variant='body2' color='text.secondary'>
              Adds a “Build with AI” button to the page builder. Describe the business and it
              writes a set of draft pages — nothing is published, and nothing you already have
              is changed.
            </Typography>
            {!configured && (
              <Typography variant='caption' color='text.secondary' display='block' mt='6px'>
                The platform has not finished setting this up. The switch will work once it has.
              </Typography>
            )}
            {quota && (
              <Stack direction='row' spacing='8px' mt='10px' flexWrap='wrap'>
                <Chip
                  size='small'
                  variant='outlined'
                  label={`${quota.remaining_included} of ${quota.included} left this month`}
                />
                {quota.credits > 0 && (
                  <Chip size='small' color='primary' variant='outlined' label={`${quota.credits} credits`} />
                )}
              </Stack>
            )}
            <Typography variant='caption' color='text.secondary' display='block' mt='8px'>
              {quota?.included ?? 0} generations are included each month. Past that you can buy
              credits, which never expire and are only used once the monthly allowance is gone.
            </Typography>
          </Box>
          <Switch
            checked={on}
            disabled={saving || !brandId || !configured}
            onChange={(e) => save(e.target.checked)}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
